import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Typography } from '@mui/material';

import { InternalPage } from 'theSource/components';
import { mapDependentsModel } from 'theSource/features/FamilyMembers/utils';
import familyImage1 from 'enrollment/assets/denny-muller-BJn4pHh_WG8-unsplash 1.png';
import familyImage2 from 'enrollment/assets/aditya-romansa-5zp0jym2w9M-unsplash 1.png';
import familyImage3 from 'enrollment/assets/annie-spratt-O1TNdLNvJLM-unsplash 1.png';
import familyImage4 from 'enrollment/assets/vasily-koloda-8CqDvPuo_kI-unsplash 1.png';
import { AddFamilyDetailsPage } from './AddFamilyDetailsPage';
import { RemovedFamilyMember } from './components/RemovedFamilyMember';
import { FamilyMemberDetails } from './components/FamilyMemberDetails';
import { ButtonLinkStyled } from './FamilyDetailsPageStyled';
import {
  BottomNavigation,
  PageContentHeaderDisplay,
} from 'enrollment/components';
import {
  selectEnrollmentContext,
  selectEnrollmentOrgPlanId,
} from 'theSource/features/Home/components/EnrollmentBanner/services';
import { PsButtonTuner } from 'stencilSetup';
import { createNewFamilyMember } from 'enrollment/features/ProfileEdit/Avatar/AvatarActions';
import { ThemedHeader } from 'components/ThemedHeader';
import {
  selectEnrollmentLatest,
  selectProfileSteps,
} from 'enrollment/components/BottomNavigation/services';
import { ROUTES } from 'enrollment/app/routes';

export interface FamilyMembersProps {
  dependentsData: any;
}

export const FamilyDetailsPage = ({ dependentsData }: FamilyMembersProps) => {
  const [openFamilyDetailsDrawer, setOpenAddFamilyDetailsDrawer] =
    useState<boolean>(false);
  const [removedDependents, setRemovedDependents] = useState<boolean>(false);
  const enrollmentContextType = useSelector(selectEnrollmentContext);
  const dispatch = useDispatch();
  const [canDecrease, setCanDecrease] = useState<boolean>(true);

  const profileSteps = useSelector(selectProfileSteps);
  const orgPlanYearId = useSelector(selectEnrollmentOrgPlanId);
  const enrollmentLatest = useSelector(selectEnrollmentLatest);

  const { t } = useTranslation('pages.enrollmentFamily');
  const navigate = useNavigate();

  const handleCloseFamilyDrawer = () => {
    setOpenAddFamilyDetailsDrawer(false);
  };

  let futurePages: string[] = [];
  if (profileSteps.questionnaire) {
    futurePages = [profileSteps.questionnaire];
  }
  if (profileSteps.decisionSupport === 'decisioniq') {
    futurePages = [...futurePages, profileSteps.decisionSupport];
  }
  if (profileSteps.chatSupport) {
    futurePages = [...futurePages, profileSteps.chatSupport]; // oliveAdvisor POC
  }

  const categories = useMemo(
    () => mapDependentsModel(dependentsData),
    [dependentsData]
  );

  const navToPath = (path: string) => {
    navigate(`/enrollment/${path}`);
  };

  const handleAddFamilyMember = () => {
    setOpenAddFamilyDetailsDrawer(true);
    dispatch(createNewFamilyMember());
  };

  useEffect(() => {
    if (removedDependents === true) {
      document.getElementById('removed-family-members')?.focus();
    } else {
      document.getElementById('show-removed-dependents-btn')?.focus();
    }
  }, [removedDependents]);

  const handleNavigateToNextPage = () => {
    let nextPage = futurePages[0] ? futurePages[0] : 'benefits';
    if (
      nextPage === profileSteps.decisionSupport ||
      (nextPage === 'benefits' &&
        profileSteps.decisionSupport === 'bundles' &&
        enrollmentLatest === 'decisionSupport')
    ) {
      nextPage = `${ROUTES.enrollment.decisionSupportRouter.path}`;
    }
    navToPath(nextPage);
  };

  const renderAddFamilyMemberBtn = () => {
    return (
      <Grid item>
        <PsButtonTuner
          buttonId="add-family-member-btn"
          label={t('addFamilyMemberTitle')}
          level="secondary"
          icon="plus"
          onClick={handleAddFamilyMember}
        />
        <AddFamilyDetailsPage
          enrollmentContextType={enrollmentContextType}
          openFamilyDetailsDrawer={openFamilyDetailsDrawer}
          handleCloseFamilyDrawer={handleCloseFamilyDrawer}
          setOpenAddFamilyDetailsDrawer={setOpenAddFamilyDetailsDrawer}
          categories={categories}
        />
      </Grid>
    );
  };

  const renderNoFamilyMemberContent = () => {
    return (
      <>
        <div data-testid="enrollment-no-dependents">
          {/* Only render the images if there are dependents */}
          {dependentsData?.data?.dependents.length > 0 && (
            <Grid container spacing={1} flexWrap="nowrap" className="my-5">
              <Grid item>
                <img
                  className="w-100"
                  src={familyImage1}
                  alt="Family member 1"
                  aria-hidden="true"
                />
              </Grid>
              <Grid item>
                <img
                  className="w-100"
                  src={familyImage2}
                  alt="Family member 2"
                  aria-hidden="true"
                />
              </Grid>
              <Grid item>
                <img
                  className="w-100"
                  src={familyImage3}
                  alt="Family member 3"
                  aria-hidden="true"
                />
              </Grid>
              <Grid item>
                <img
                  className="w-100"
                  src={familyImage4}
                  alt="Family member 4"
                  aria-hidden="true"
                />
              </Grid>
            </Grid>
          )}
        </div>
        <Grid container spacing={6} justifyContent="center">
          {renderAddFamilyMemberBtn()}
        </Grid>
      </>
    );
  };

  return (
    <InternalPage
      id="enrollment-family-container"
      testId="enrollment-family-details-page"
      blockClasses="mt-lg-4_5 mt-md-5 px-4 pt-3 pb-4 pt-md-3_5 p-md-0 pt-lg-0"
    >
      <ThemedHeader>{t('title')}</ThemedHeader>
      <PageContentHeaderDisplay
        pageContentLookupCode="enroll_dependents"
        displayPageHeader={false}
        orgPlanYearId={orgPlanYearId}
      />
      {!canDecrease ? (
        <div
          id="cannot-delete-notice"
          className="blue-alert mt-4"
          data-testid="cannot-delete-notice"
        >
          <i
            className="ph ph-info mr-2 my-sm-auto"
            aria-hidden="true"
            role="presentation"
          ></i>
          <p className="mb-0">{t('cannotRemoveDependent')}</p>
        </div>
      ) : (
        <></>
      )}
      {dependentsData?.data?.dependents.length === 0 ? (
        renderNoFamilyMemberContent()
      ) : (
        <FamilyMemberDetails
          data={dependentsData?.data?.dependents}
          dependentsData={dependentsData}
          enrollmentContextType={enrollmentContextType}
          setCanDecrease={setCanDecrease}
        >
          {renderAddFamilyMemberBtn()}
        </FamilyMemberDetails>
      )}
      {dependentsData?.data?.inactive_dependents?.length > 0 ? (
        <>
          <hr className="divider mt-5 mb-4" />
          <h1>{t('removeDependentSectionTitle')}</h1>
          <Typography variant="body1">
            {t('removeDependentSectionDescription')}
          </Typography>
          {removedDependents ? (
            <RemovedFamilyMember
              data={dependentsData?.data?.inactive_dependents}
              dependentsData={dependentsData}
              enrollmentContextType={enrollmentContextType}
            ></RemovedFamilyMember>
          ) : (
            <></>
          )}
          <ButtonLinkStyled>
            <PsButtonTuner
              buttonId="show-removed-dependents-btn"
              label={
                !removedDependents
                  ? t('showRemovedDependentsLabel')
                  : t('hideRemovedDependentsLabel')
              }
              level="tertiary"
              onClick={() => setRemovedDependents(!removedDependents)}
            />
          </ButtonLinkStyled>
        </>
      ) : (
        <></>
      )}
      <BottomNavigation
        completePages={['profile']}
        futurePages={futurePages}
        onPage="family"
        translatedPage="pages.enrollmentProfile"
        onClickEvent={handleNavigateToNextPage}
      />
    </InternalPage>
  );
};
